import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CPage, CRow, CPanel, Text } from '@appcues/component-library';
import {
  ContentStatus,
  H1,
  FULL_RANGE_OPTIONS,
} from '@studio/legacy-components';
import useDateRangeQuery from 'next/hooks/use-date-range-query';
import TimestampRefresh from 'next/components/TimestampRefresh';
import BoundedDateRangeDropdown from 'next/components/BoundedDateRangeDropdown';
import { selectFlowHistoricalLimit } from 'selectors/timeframe-options';
import ChildPage from 'components/Common/ChildPage';
import Loader from 'components/Common/Loader';
import { clear } from 'actions/analytics-v2';
import { selectFlow, selectAreFlowsSynced } from 'reducers/account/flows';
import EmptyState from './EmptyState';
import FlowAnalyticsActions from './FlowAnalyticsActions';
import FlowActivityChart from './FlowActivityChart';
import PreviewCarousel from './PreviewCarousel';
import StepByStepTable from './StepByStepTable';
import RecentUsersTable from './RecentUsersTable';
import IssuesTable from './IssuesTable';
import Scoreboard from './Scoreboard';

export const FlowAnalytics = ({
  flow,
  limit,
  loaded,
  onRefresh,
  options = FULL_RANGE_OPTIONS,
}) => {
  const [updated, setUpdated] = useState(Date.now());

  const [{ range, start, end }, setDateRange] = useDateRangeQuery(options, {
    range: 7,
  });

  if (!loaded) {
    return <Loader />;
  }

  const startTime = start.getTime();
  const endTime = end.getTime();

  const handleRefresh = () => {
    setUpdated(Date.now());
    onRefresh();
  };

  return (
    <ChildPage
      createdAt={flow.createdAt}
      createdByUserId={flow.createdBy}
      updatedAt={flow.updatedAt}
      updatedByUserId={flow.updatedBy}
      breadcrumbContents={{
        label: 'Flows',
        path: '/flows',
        child: {
          label: `${flow.name}`,
        },
      }}
      components={{
        Title: function Header() {
          return (
            <>
              <H1 title={flow.name}>{flow.name}</H1>
              <ContentStatus published={flow.published} state={flow.state} />
            </>
          );
        },
      }}
      subHeaderActions={
        <FlowAnalyticsActions
          flowId={flow.id}
          isLocalized={flow.lockedForLocalization}
          createdAt={flow.createdAt}
        />
      }
    >
      <CPage.Container>
        {flow.publishedAt && (
          <CRow>
            <BoundedDateRangeDropdown
              limit={limit}
              onApply={setDateRange}
              options={options}
              value={{ range, start, end }}
            />
            <TimestampRefresh endTime={updated} onClick={handleRefresh} />
          </CRow>
        )}
        <CRow marginTop="16px">
          <CPanel>
            <Text type="tertiary" marginBottom="10px" alignSelf="end">
              {flow.publishedAt
                ? 'Displaying most recently published version'
                : 'Displaying most recent draft'}
            </Text>
            <PreviewCarousel flowId={flow.id} />
          </CPanel>
        </CRow>
        {flow.publishedAt ? (
          <>
            <CRow marginTop="30px">
              <CPanel title="Flow analytics">
                <p>Key metrics about your flow performance</p>
                <FlowActivityChart
                  flowId={flow.id}
                  startTime={startTime}
                  endTime={endTime}
                />
              </CPanel>
            </CRow>
            <CRow marginTop="30px">
              <Scoreboard
                flowId={flow.id}
                startTime={startTime}
                endTime={endTime}
              />
            </CRow>
            <CRow marginTop="30px">
              <StepByStepTable
                flowId={flow.id}
                startTime={startTime}
                endTime={endTime}
              />
            </CRow>
            <CRow marginTop="30px">
              <IssuesTable
                flowId={flow.id}
                startTime={startTime}
                endTime={endTime}
              />
            </CRow>
            <CRow marginTop="30px">
              <RecentUsersTable
                flowId={flow.id}
                startTime={startTime}
                endTime={endTime}
              />
            </CRow>
          </>
        ) : (
          <CRow marginTop="30px">
            <EmptyState />
          </CRow>
        )}
      </CPage.Container>
    </ChildPage>
  );
};

FlowAnalytics.propTypes = {
  flow: PropTypes.shape({
    createdAt: PropTypes.number,
    createdBy: PropTypes.string,
    id: PropTypes.string,
    lockedForLocalization: PropTypes.bool,
    name: PropTypes.string,
    published: PropTypes.bool,
    publishedAt: PropTypes.number,
    state: PropTypes.string,
    updatedAt: PropTypes.number,
    updatedBy: PropTypes.string,
  }),
  limit: PropTypes.number,
  loaded: PropTypes.bool,
  onRefresh: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
};

const mapStateToProps = (
  state,
  {
    match: {
      params: { stepId: flowId },
    },
  }
) => ({
  flow: selectFlow(state, flowId),
  limit: selectFlowHistoricalLimit(state),
  loaded: selectAreFlowsSynced(state),
});

const mapDispatchToProps = {
  onRefresh: clear,
};

export default connect(mapStateToProps, mapDispatchToProps)(FlowAnalytics);
